import { useMemo, useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import { useCaseTaskHistory, useCaseTasks } from "@/lib/CaseTasksContext";
import { useCurrentProfile } from "@/lib/CurrentProfileContext";
import { useTeam } from "@/lib/useTeam";
import {
  canCompleteTask,
  canReopenTask,
  caseTaskLabel,
  formatTaskDuration,
  isTaskOpen,
  taskDurationMs,
  type CaseTask,
} from "@/lib/caseTasks";
import { firstNameOf, oldestTasksFirst, timeAgo } from "@/lib/kanbanTasks";
import { formatParisDate, formatParisTime } from "@/lib/parisDateTime";
import { useNow } from "@/lib/useNow";
import { TaskFlame } from "./TaskFlame";

export interface CaseTasksBlockProps {
  caseId: string;
  caseCenter: string | null;
}

const when = (iso: string) =>
  `le ${formatParisDate(iso, { day: "2-digit", month: "2-digit" })} à ${formatParisTime(iso)}`;

/** Bloc « Tâches » en haut de la colonne Relances de la fiche dossier. */
export function CaseTasksBlock({ caseId, caseCenter }: CaseTasksBlockProps) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const { isManager } = useCurrentProfile();
  const { myCenters, completeTask, reopenTask, cancelTask } = useCaseTasks();
  const { profilesById } = useTeam();
  const { tasks, loading, error } = useCaseTaskHistory(caseId);
  const now = useNow(30_000);
  const [showClosed, setShowClosed] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const open = useMemo(() => oldestTasksFirst(tasks.filter(isTaskOpen)), [tasks]);
  const closed = useMemo(
    () =>
      tasks
        .filter((task) => !isTaskOpen(task))
        .sort((a, b) => ((b.completed_at ?? b.cancelled_at ?? "") > (a.completed_at ?? a.cancelled_at ?? "") ? 1 : -1)),
    [tasks],
  );

  if (!isManager && tasks.length === 0) return null;

  const name = (id: string | null) => (id && id === userId ? "vous" : firstNameOf(id ? profilesById.get(id) : null));
  const forWhom = (task: CaseTask) =>
    task.assigned_center
      ? `Équipe ${task.assigned_center}`
      : task.assigned_to === userId
        ? "Pour moi"
        : `Pour ${firstNameOf(profilesById.get(task.assigned_to ?? ""))}`;

  const run = async (taskId: string, action: (id: string) => Promise<unknown>) => {
    setBusyId(taskId);
    setActionError(null);
    try {
      await action(taskId);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Une erreur est survenue. Réessayez.");
    } finally {
      setBusyId(null);
      setConfirmCancel(null);
    }
  };

  return (
    <section aria-labelledby={`case-tasks-${caseId}`} className="border-b border-gray-200">
      <div className="case-column-header flex items-center justify-between gap-2 px-4">
        <h3 id={`case-tasks-${caseId}`} className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
          <TaskFlame className="h-4 w-4" animated={open.length > 0} />
          Tâches
          {open.length > 0 && (
            <span className="rounded-full bg-orange-100 px-1.5 py-0.5 text-[11px] font-bold leading-none text-orange-800">{open.length}</span>
          )}
        </h3>
      </div>

      <div className="space-y-2 px-4 pb-3 pt-2">
        {(error || actionError) && (
          <p role="alert" className="rounded-md bg-red-50 px-2.5 py-1.5 text-xs text-red-700">{actionError || error}</p>
        )}

        {loading && tasks.length === 0 ? (
          <p className="text-xs text-gray-400">Chargement des tâches…</p>
        ) : open.length === 0 ? (
          <p className="text-xs text-gray-500">
            Aucune tâche ouverte.
            {isManager && " Clic droit sur la carte du dossier dans le Kanban pour en assigner une."}
            {isManager && !caseCenter && " Sans centre, seule l'option « Moi » est possible."}
          </p>
        ) : (
          <ul className="space-y-2">
            {open.map((task) => {
              const checkable = canCompleteTask(task, userId, isManager, myCenters);
              const busy = busyId === task.id;
              return (
                <li key={task.id} className="rounded-lg border border-orange-200 bg-orange-50/50 p-2.5">
                  <div className="flex items-start gap-2.5">
                    <input
                      type="checkbox"
                      checked={false}
                      disabled={!checkable || busy}
                      onChange={() => void run(task.id, completeTask)}
                      className="mt-0.5 h-4 w-4 flex-shrink-0 cursor-pointer accent-emerald-600 disabled:cursor-not-allowed disabled:opacity-40"
                      aria-label={`Marquer « ${caseTaskLabel(task.task_type)} » comme fait`}
                      title={checkable ? "Marquer comme fait" : "Seule l'équipe concernée ou le manager peut cocher"}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900">{caseTaskLabel(task.task_type)}</p>
                      <p className="text-xs text-gray-500">
                        {forWhom(task)} · par {name(task.created_by)} · {timeAgo(task.created_at, now)}
                      </p>
                      {task.note && <p className="mt-1 whitespace-pre-line text-xs text-gray-700">{task.note}</p>}
                      {isManager && (
                        confirmCancel === task.id ? (
                          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
                            <span className="text-gray-700">Annuler cette tâche ?</span>
                            <button type="button" disabled={busy} onClick={() => void run(task.id, cancelTask)} className="rounded px-1.5 py-0.5 font-semibold text-red-700 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500">
                              Oui, annuler
                            </button>
                            <button type="button" onClick={() => setConfirmCancel(null)} className="rounded px-1.5 py-0.5 text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
                              Non
                            </button>
                          </div>
                        ) : (
                          <button type="button" onClick={() => setConfirmCancel(task.id)} className="mt-1 rounded px-1 text-xs text-gray-500 underline-offset-2 hover:text-red-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
                            Annuler
                          </button>
                        )
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {closed.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => setShowClosed((v) => !v)}
              aria-expanded={showClosed}
              className="flex items-center gap-1 rounded text-xs font-medium text-gray-600 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              <svg viewBox="0 0 24 24" className={`h-3.5 w-3.5 transition-transform ${showClosed ? "rotate-90" : ""}`} fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Terminées ({closed.length})
            </button>
            {showClosed && (
              <ul className="mt-1.5 space-y-1.5">
                {closed.map((task) => (
                  <li key={task.id} className="rounded-md bg-gray-50 px-2.5 py-2 text-xs">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`font-medium ${task.cancelled_at ? "text-gray-400 line-through" : "text-gray-700"}`}>
                        {task.completed_at && <span className="mr-1 text-emerald-600" aria-hidden="true">✓</span>}
                        {caseTaskLabel(task.task_type)}
                      </p>
                      {canReopenTask(task, userId, isManager) && (
                        <button
                          type="button"
                          disabled={busyId === task.id}
                          onClick={() => void run(task.id, reopenTask)}
                          className="flex-shrink-0 rounded px-1 font-medium text-indigo-700 hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-50"
                        >
                          Rouvrir
                        </button>
                      )}
                    </div>
                    <p className="mt-0.5 text-gray-500">
                      {task.completed_at
                        ? `Cochée par ${name(task.completed_by)} ${when(task.completed_at)} · en ${formatTaskDuration(taskDurationMs(task))}`
                        : `Annulée par ${name(task.cancelled_by)} ${when(task.cancelled_at ?? task.created_at)}`}
                    </p>
                    <p className="text-gray-400">{forWhom(task)} · créée par {name(task.created_by)}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
